/**
 * useSseStream - React hook for subscribing to a server-sent events endpoint.
 *
 * DEFERRED_ITEMS #10. Wraps the EventSource API with:
 *
 *   - exponential-backoff reconnect (caps at 30s, resets on success)
 *   - a fixed-size ring buffer of recent events (default 50)
 *   - paused / resumed state controlled by the caller
 *   - automatic close on unmount
 *
 * Usage:
 *
 *   const { events, paused, setPaused, connected } = useSseStream<MyEvent>({
 *     url: '/api/v1/activity/events/stream',
 *     bufferSize: 50,
 *     parse: (raw) => MySchema.parse(raw),  // optional zod validation
 *   });
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { API_BASE } from '@/lib/api';

/**
 * Resolve a `/api/...` stream URL through the configured API base so bundled
 * Pattern-A tenants hit Discovery's controller (VITE_API_BASE=/api/discovery,
 * Core rewrites /api/discovery/* → /api/*) instead of colliding with Core on
 * the bare /api/v1/* prefix. Identity in legacy standalone (API_BASE=/api).
 */
function resolveStreamUrl(url: string): string {
  return url.startsWith('/api/') ? `${API_BASE}${url.slice('/api'.length)}` : url;
}

export interface UseSseStreamOptions<Event> {
  url: string;
  /** Maximum number of events to retain. Default 50. */
  bufferSize?: number;
  /** Optional parser/validator. Throws → event dropped + parseErrors increments. */
  parse?: (raw: unknown) => Event;
  /** Optional filter for SSE event names (e.g. only 'decision.*'). Default: accept all. */
  acceptEventName?: (name: string) => boolean;
  /** Start paused? Default false. */
  initialPaused?: boolean;
}

export interface UseSseStreamResult<Event> {
  events: Array<{ id: string; eventName: string; data: Event }>;
  paused: boolean;
  setPaused: (paused: boolean) => void;
  connected: boolean;
  errorCount: number;
  parseErrors: number;
  clear: () => void;
}

export function useSseStream<Event = unknown>(
  opts: UseSseStreamOptions<Event>,
): UseSseStreamResult<Event> {
  const bufferSize = opts.bufferSize ?? 50;
  const [events, setEvents] = useState<
    Array<{ id: string; eventName: string; data: Event }>
  >([]);
  const [paused, setPaused] = useState(opts.initialPaused ?? false);
  const [connected, setConnected] = useState(false);
  const [errorCount, setErrorCount] = useState(0);
  const [parseErrors, setParseErrors] = useState(0);
  const sourceRef = useRef<EventSource | null>(null);
  const backoffRef = useRef(1_000);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 2026-05-26 fix (BUG-2): snapshot callback props into refs so the
  // connect effect only re-runs when *stable* deps change (url / paused
  // / bufferSize). The previous version listed `opts.parse` and
  // `opts.acceptEventName` directly - callers pass these as inline
  // arrow functions that get a fresh reference on every parent render,
  // which tore down + recreated the EventSource on every render. In
  // prod that meant ~50 connection attempts per second, instantly
  // burning the api-controller's 600-req/24s rate limit and getting
  // stuck in a 429-loop forever - Stream page showed "Connecting..."
  // indefinitely with 0 events despite 4,622 in ClickHouse.
  const parseRef = useRef(opts.parse);
  const acceptEventNameRef = useRef(opts.acceptEventName);
  useEffect(() => {
    parseRef.current = opts.parse;
    acceptEventNameRef.current = opts.acceptEventName;
  }, [opts.parse, opts.acceptEventName]);

  const clear = useCallback(() => setEvents([]), []);

  useEffect(() => {
    if (paused) {
      // Tear the EventSource down while paused so the server isn't holding
      // an idle socket - the streamer's Last-Event-ID resume handles the
      // gap when we reconnect.
      if (sourceRef.current) {
        sourceRef.current.close();
        sourceRef.current = null;
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      setConnected(false);
      return;
    }

    let cancelled = false;

    const connect = (): void => {
      if (cancelled) return;
      // EventSource doesn't allow custom request headers, so pass the
      // bearer token as a query parameter. The api-controller's
      // tenant-context plugin accepts ?access_token= as a fallback.
      let token: string | null = null;
      try {
        token = localStorage.getItem('foctta.auth.token');
      } catch {
        /* ignore */
      }
      const resolvedUrl = resolveStreamUrl(opts.url);
      const sep = resolvedUrl.includes('?') ? '&' : '?';
      const fullUrl = token
        ? `${resolvedUrl}${sep}access_token=${encodeURIComponent(token)}`
        : resolvedUrl;
      const source = new EventSource(fullUrl, { withCredentials: true });
      sourceRef.current = source;

      source.onopen = (): void => {
        if (cancelled) return;
        setConnected(true);
        backoffRef.current = 1_000;
      };

      source.onerror = (): void => {
        setConnected(false);
        setErrorCount((c) => c + 1);
        source.close();
        sourceRef.current = null;
        if (cancelled) return;
        const next = Math.min(backoffRef.current * 2, 30_000);
        reconnectTimerRef.current = setTimeout(connect, backoffRef.current);
        backoffRef.current = next;
      };

      // The streamer emits named events (event: decision.allow, finding.ueba,
      // ...). We attach a generic listener via `addEventListener` since the
      // default `onmessage` only catches the default 'message' event type.
      const handleAny = (e: MessageEvent): void => {
        const acceptEventName = acceptEventNameRef.current;
        const parse = parseRef.current;
        if (!acceptEventName || acceptEventName(e.type)) {
          let parsed: Event;
          try {
            const raw = JSON.parse(e.data);
            parsed = parse ? parse(raw) : (raw as Event);
          } catch {
            setParseErrors((p) => p + 1);
            return;
          }
          setEvents((prev) => {
            const next = [{ id: e.lastEventId, eventName: e.type, data: parsed }, ...prev];
            return next.slice(0, bufferSize);
          });
        }
      };
      // Discover names lazily - we listen to every `addEventListener` call.
      // EventSource doesn't have a "wildcard" listener, so we register the
      // common names we expect from the API. New names can be added here.
      const knownNames = [
        'message',
        'ready',
        'decision.allow',
        'decision.mask',
        'decision.step_up',
        'decision.block',
        'finding.ueba',
        'finding.sonar',
        'finding.early_warning',
      ];
      for (const name of knownNames) source.addEventListener(name, handleAny as EventListener);
      // Generic fallback for any unknown event name.
      source.onmessage = handleAny;
    };

    connect();

    return (): void => {
      cancelled = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (sourceRef.current) {
        sourceRef.current.close();
        sourceRef.current = null;
      }
      setConnected(false);
    };
    // Deliberately exclude parse + acceptEventName - they're read via
    // refs inside connect() so callers can pass inline arrow functions
    // without triggering a reconnect storm.
  }, [opts.url, paused, bufferSize]);

  return { events, paused, setPaused, connected, errorCount, parseErrors, clear };
}
